import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  Users,
  MessageSquare,
  Star,
  HelpCircle,
  Settings,
  Coins,
  GitMerge,
  FileText,
  Bell,
  BellDot,
  CreditCard,
  Receipt,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  List,
  ShieldCheck,
  Tag,
  Trash2,
  ScrollText
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import clsx from 'clsx'
import { useState, useEffect } from 'react'

export default function Sidebar() {
  const { user, isAdmin, isSuperAdmin } = useAuth()
  const navigate = useNavigate()

  // Check screen size and set initial collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    if (saved !== null) return saved === 'true'
    return window.innerWidth < 1024 // Collapse on screens smaller than 1024px
  })

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed))
  }, [isCollapsed])

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && !isCollapsed) {
        setIsCollapsed(true)
      } else if (window.innerWidth >= 1024 && isCollapsed) {
        setIsCollapsed(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/dashboard/my-barters', label: 'My Barters', icon: List },
    { to: '/dashboard/barters', label: 'Marketplace', icon: Package },
    { to: '/dashboard/groups', label: 'Barter Groups', icon: GitMerge },
    { to: '/dashboard/requests', label: 'Requests', icon: FileText },
    { to: '/dashboard/coins', label: 'Barter Coins', icon: Coins },
    { to: '/dashboard/messages', label: 'Messages', icon: MessageSquare },
    { to: '/dashboard/reviews', label: 'Reviews', icon: Star },
    { to: '/dashboard/billing', label: 'Billing', icon: Receipt },
    { to: '/dashboard/support', label: 'Support', icon: HelpCircle },
  ]

  // Add analytics link for paid tier users
  if (user?.subscription_tier === 'paid' && user?.subscription_status === 'active') {
    navItems.push({
      to: '/dashboard/analytics',
      label: 'Analytics',
      icon: BarChart3
    })
  }

  // Add subscription link for professional users
  if (user?.user_type === 'professional') {
    navItems.push({
      to: '/dashboard/subscription',
      label: (user?.subscription_status === 'active' && user?.subscription_tier === 'paid') ? 'Subscription' : 'Upgrade',
      icon: CreditCard
    })
  }

  // Add admin-only items
  if (isAdmin()) {
    navItems.push({ to: '/dashboard/users', label: 'Users', icon: Users })
  }

  // Add super admin-only items
  if (isSuperAdmin()) {
    navItems.push({ to: '/dashboard/statistics', label: 'Statistics', icon: BarChart3 })
    navItems.push({ to: '/dashboard/withdrawals', label: 'Withdrawals', icon: DollarSign })
    navItems.push({ to: '/dashboard/escrow', label: 'Escrow Management', icon: ShieldCheck })
    navItems.push({ to: '/dashboard/coupons', label: 'Coupons', icon: Tag })
    navItems.push({ to: '/dashboard/notifications', label: 'Email Notifications', icon: Bell })
    navItems.push({ to: '/dashboard/page-notifications', label: 'Page Notifications', icon: BellDot })
    navItems.push({ to: '/dashboard/audit-logs', label: 'Audit Logs', icon: ScrollText })
    navItems.push({ to: '/dashboard/data-purge', label: 'Data Purge', icon: Trash2 })
    navItems.push({ to: '/dashboard/settings', label: 'Settings', icon: Settings })
  }

  return (
    <aside
      className={clsx(
        'bg-white min-h-screen shadow-sm border-r border-gray-200 transition-all duration-300 relative flex-shrink-0',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Collapse/Expand Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 z-10 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors shadow-md"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Logo/Brand */}
      <div className={clsx('p-6 transition-all', isCollapsed && 'px-3')}>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-left w-full hover:opacity-80 transition-opacity"
        >
          {isCollapsed ? (
            <div className="flex justify-center">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-primary-600">BarterSheet</h1>
              <p className="text-sm text-gray-500 mt-1">Barter Platform</p>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="px-3 py-2">
        {navItems.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 py-2.5 rounded-lg mb-1 transition-colors group relative',
                  isCollapsed ? 'justify-center' : 'space-x-3',
                  isActive
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
              title={isCollapsed ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                  {item.label}
                </div>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* User Profile */}
      {user && (
        <div
          className={clsx(
            'absolute bottom-0 p-4 border-t border-gray-200 bg-gray-50 transition-all',
            isCollapsed ? 'w-20' : 'w-64'
          )}
        >
          <button
            onClick={() => navigate('/dashboard/profile')}
            className={clsx(
              'flex items-center w-full hover:bg-gray-100 rounded-lg p-2 -m-2 transition-colors group relative',
              isCollapsed ? 'justify-center' : 'space-x-3'
            )}
            title={isCollapsed ? `${user.first_name} ${user.last_name}` : undefined}
          >
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {user.first_name.charAt(0)}{user.last_name.charAt(0)}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  {user.subscription_tier === 'paid' && user.subscription_status === 'active' && (
                    <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded">Pro</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 capitalize">{user.user_type.replace('_', ' ')}</p>
              </div>
            )}

            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                {user.first_name} {user.last_name}
                <br />
                <span className="text-xs text-gray-300 capitalize">{user.user_type.replace('_', ' ')}</span>
              </div>
            )}
          </button>
        </div>
      )}
    </aside>
  )
}
